import ActivityTypeHelper from '../types/activity-types';
import { ITripActivity } from '../Models/trips/trip-activity.model';

/**
 * Service for managing activity visual appearance with fallback logic.
 *
 * This service handles the fallback chain for activity colors and icons:
 * 1. Custom appearance (from activity.customization)
 * 2. Type-based default (from ActivityTypeHelper)
 *
 * @example
 * ```typescript
 * // Get color with fallback
 * const color = ActivityAppearanceService.getColor(activity);
 *
 * // Get icon with fallback
 * const icon = ActivityAppearanceService.getIcon(activity);
 *
 * // Check if activity has custom appearance
 * if (ActivityAppearanceService.hasCustomAppearance(activity)) {
 *   // Show custom appearance badge
 * }
 * ```
 */
export class ActivityAppearanceService {
  /**
   * Get the effective color for an activity.
   * Priority: custom color → type default
   *
   * @param activity - Activity object, minimal type info, or type string
   * @returns Hex color code (e.g., "#842234")
   */
  static getColor(
    activity?:
      | string
      | ITripActivity
      | { type?: string; customization?: { color?: string } }
  ): string {
    // Handle legacy string type parameter
    if (typeof activity === 'string') {
      return ActivityTypeHelper.getColor(activity);
    }

    // Handle null/undefined
    if (!activity) {
      return ActivityTypeHelper.getColor('other');
    }

    // 1. Use custom color if set
    if (activity.customization?.color) {
      return activity.customization.color;
    }

    // 2. Fall back to type default
    return ActivityTypeHelper.getColor(activity.type || 'other');
  }

  /**
   * Get the effective icon/symbol for an activity.
   * Priority: custom icon → type default
   *
   * @param activity - Activity object, minimal type info, or type string
   * @returns Icon identifier (e.g., "restaurant", "hotel")
   */
  static getIcon(
    activity?:
      | string
      | ITripActivity
      | { type?: string; customization?: { icon?: string } }
  ): string {
    // Handle legacy string type parameter
    if (typeof activity === 'string') {
      return ActivityTypeHelper.getSymbol(activity);
    }

    // Handle null/undefined
    if (!activity) {
      return ActivityTypeHelper.getSymbol('other');
    }

    // 1. Use custom icon if set
    if (activity.customization?.icon) {
      return activity.customization.icon;
    }

    // 2. Fall back to type default
    return ActivityTypeHelper.getSymbol(activity.type || 'other');
  }

  /**
   * Check if an activity has any custom appearance settings.
   *
   * @param activity - Activity object to check
   * @returns True if activity has custom color or icon
   */
  static hasCustomAppearance(activity?: ITripActivity | null): boolean {
    if (!activity) return false;
    return !!(activity.customization?.color || activity.customization?.icon);
  }

  /**
   * Check if an activity has a custom color.
   *
   * @param activity - Activity object to check
   * @returns True if activity has custom color
   */
  static hasCustomColor(activity?: ITripActivity | null): boolean {
    if (!activity) return false;
    return !!activity.customization?.color;
  }

  /**
   * Check if an activity has a custom icon.
   *
   * @param activity - Activity object to check
   * @returns True if activity has custom icon
   */
  static hasCustomIcon(activity?: ITripActivity | null): boolean {
    if (!activity) return false;
    return !!activity.customization?.icon;
  }

  /**
   * Clear all customization from an activity (returns new object).
   *
   * @param activity - Activity to clear customization from
   * @returns New activity object without customization
   */
  static clearCustomization(activity: ITripActivity): ITripActivity {
    const { customization, ...rest } = activity;
    return rest;
  }

  /**
   * Create or update customization for an activity (returns new object).
   *
   * @param activity - Activity to update
   * @param color - Optional hex color code
   * @param icon - Optional icon identifier
   * @returns New activity object with customization
   * @throws Error if color format is invalid
   */
  static setCustomization(
    activity: ITripActivity,
    color?: string,
    icon?: string
  ): ITripActivity {
    // Validate color format if provided
    if (color && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)) {
      throw new Error(
        'Invalid color format. Use hex format (e.g., #FF5733 or #f57)'
      );
    }

    // If neither color nor icon provided, clear customization
    if (!color && !icon) {
      return this.clearCustomization(activity);
    }

    return {
      ...activity,
      customization: {
        ...(color && { color }),
        ...(icon && { icon })
      }
    };
  }

  /**
   * Get type default color for an activity (ignores custom color).
   *
   * @param activity - Activity object or type string
   * @returns Hex color code from type defaults
   */
  static getTypeDefaultColor(activity: string | ITripActivity): string {
    const type = typeof activity === 'string' ? activity : activity.type;
    return ActivityTypeHelper.getColor(type || 'other');
  }

  /**
   * Get type default icon for an activity (ignores custom icon).
   *
   * @param activity - Activity object or type string
   * @returns Icon identifier from type defaults
   */
  static getTypeDefaultIcon(activity: string | ITripActivity): string {
    const type = typeof activity === 'string' ? activity : activity.type;
    return ActivityTypeHelper.getSymbol(type || 'other');
  }
}

export default ActivityAppearanceService;
